import { useState } from "react";
import { Basket, Tree, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FLAGS = [
  ["children_ok", "Children welcome"],
  ["dogs_ok", "Dogs on a lead"],
  ["wheelchair_access", "Wheelchair access"],
  ["parking", "Parking on site"],
  ["picnic_area", "Picnic area"],
  ["tractor_ride", "Tractor ride"],
  ["honesty_box", "Honesty box"],
];

// Pick your own: apples, peppers, onions — book a slot, pay now, at the gate, or by weight.
export const PickYourOwn = ({ data, mine, busy, act, reload }) => {
  const [field, setField] = useState({
    crop: "apples", title: "", price_basis: "per kg", price: 0, open_from: "", open_to: "",
    days: ["Saturday", "Sunday"], slot_times: "09:00, 13:00", people_per_slot: 20, bring: "",
    children_ok: true, dogs_ok: false, wheelchair_access: false, parking: true,
    picnic_area: false, tractor_ride: false, honesty_box: false, note: "",
  });
  const [book, setBook] = useState({});
  const [weights, setWeights] = useState({});

  const toggleDay = (d) => setField({
    ...field,
    days: field.days.includes(d) ? field.days.filter((x) => x !== d) : [...field.days, d],
  });

  return (
    <div className="space-y-4" data-testid="pyo-section">
      <div>
        <p className="overline text-slate-500">
          <Basket size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Pick your own
        </p>
        {(data.fields || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No rows open for picking yet.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {data.fields.map((f) => (
              <div key={f.id} className="border border-border bg-white p-3" data-testid={`field-${f.id}`}>
                {f.image_url && (
                  <img src={`${BACKEND}${f.image_url}`} alt={f.title} loading="lazy"
                    className="mb-2 h-32 w-full border border-border object-cover" />
                )}
                <p className="text-sm font-semibold text-[#0F172A]">
                  <Tree size={14} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />{f.title}
                </p>
                <p className="text-xs text-slate-500">
                  {f.crop} · {f.stall_name} · {f.where} · {(f.days || []).join(", ")} · {(f.slot_times || []).join(" / ")}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {f.price_basis === "pay by weight at the gate"
                    ? `${f.symbol}${Number(f.price || 0).toFixed(2)} a kg, weighed at the gate`
                    : `${f.symbol}${Number(f.price || 0).toFixed(2)} ${f.price_basis}`}
                </p>
                <p className="text-xs text-slate-500">
                  {FLAGS.filter(([k]) => f[k]).map(([, label]) => label).join(" · ") || "Just bring a bag"}
                  {f.bring ? ` · bring ${f.bring}` : ""}
                </p>
                {f.owner_id !== data.me.id && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <select data-testid={`field-day-${f.id}`} aria-label="Day"
                        value={book[f.id]?.day || (f.days || [])[0] || ""}
                        onChange={(e) => setBook({ ...book, [f.id]: { ...book[f.id], day: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {(f.days || []).map((d) => <option key={d} value={d}>{String(d)}</option>)}
                      </select>
                      <select data-testid={`field-slot-${f.id}`} aria-label="Slot"
                        value={book[f.id]?.slot || (f.slot_times || [])[0] || ""}
                        onChange={(e) => setBook({ ...book, [f.id]: { ...book[f.id], slot: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {(f.slot_times || []).map((t) => <option key={t} value={t}>{String(t)}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        People
                        <Input data-testid={`field-people-${f.id}`} type="number" min={1} className="w-16"
                          value={book[f.id]?.people ?? 2}
                          onChange={(e) => setBook({ ...book, [f.id]: { ...book[f.id], people: e.target.value } })} />
                      </label>
                      <select data-testid={`field-pay-${f.id}`} aria-label="Pay"
                        value={book[f.id]?.pay || (f.price_basis === "pay by weight at the gate" ? "by_weight" : "online")}
                        onChange={(e) => setBook({ ...book, [f.id]: { ...book[f.id], pay: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {f.price_basis !== "pay by weight at the gate" && <option value="online">Pay online now</option>}
                        <option value="at_gate">Pay at the gate</option>
                        <option value="by_weight">Pay for what I pick</option>
                      </select>
                    </div>
                    <Button size="sm" data-testid={`field-book-${f.id}`} disabled={busy}
                      onClick={() => act(async () => {
                        const b = book[f.id] || {};
                        const pay = b.pay || (f.price_basis === "pay by weight at the gate" ? "by_weight" : "online");
                        const { data: made } = await api.post("/farm/fields/book", {
                          field_id: f.id, day: b.day || (f.days || [])[0],
                          slot: b.slot || (f.slot_times || [])[0],
                          people: Number(b.people) || 2, pay,
                        });
                        if (pay === "online" && Number(made.total) > 0) {
                          const { data: p } = await api.post("/payments/farm/picking/checkout", {
                            booking_id: made.id, origin_url: window.location.origin });
                          if (p.url) window.location.href = p.url;
                        }
                      }, "Slot booked — show your reference at the gate")}>
                      <Ticket size={14} className="mr-1.5" /> Book a slot
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.my_bookings || []).length > 0 && (
        <div data-testid="pyo-my-bookings">
          <p className="overline text-slate-500">Your picking slots</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.my_bookings.map((b) => (
              <li key={b.id} data-testid={`booking-${b.id}`}>
                {b.reference} · {b.title} · {b.day} {b.slot} · {b.people} people ·
                {" "}{b.weight_kg ? `${b.weight_kg} kg picked · ` : ""}
                {b.symbol}{Number(b.total || 0).toFixed(2)} · {b.status} · {b.payment_status}
                {b.pay === "online" && b.payment_status === "unpaid" && Number(b.total) > 0 && (
                  <Button className="ml-2" size="sm" data-testid={`booking-pay-${b.id}`} disabled={busy}
                    onClick={async () => {
                      try {
                        const { data: p } = await api.post("/payments/farm/picking/checkout", {
                          booking_id: b.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      } catch (e) { oops(e); }
                    }}>
                    Pay now
                  </Button>
                )}
                {b.status === "booked" && (
                  <Button className="ml-2" size="sm" variant="outline" data-testid={`booking-cancel-${b.id}`}
                    disabled={busy}
                    onClick={() => act(() => api.post(`/farm/bookings/${b.id}/cancel`), "Cancelled")}>
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.field_bookings || []).length > 0 && (
        <div data-testid="pyo-field-bookings">
          <p className="overline text-slate-500">Pickers coming to your field</p>
          <div className="mt-2 space-y-2">
            {data.field_bookings.map((b) => (
              <div key={b.id} className="border border-border bg-white p-3 text-sm"
                data-testid={`field-booking-${b.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {b.reference} · {b.name} · {b.title} · {b.day} {b.slot} · {b.people} people · {b.status}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-slate-600">
                    Weighed kg
                    <Input data-testid={`field-weight-${b.id}`} type="number" min={0} step="0.1" className="w-20"
                      value={weights[b.id] ?? ""} onChange={(e) => setWeights({ ...weights, [b.id]: e.target.value })} />
                  </label>
                  <Button size="sm" variant="outline" data-testid={`field-weigh-${b.id}`} disabled={busy}
                    onClick={() => act(async () => {
                      await api.post(`/farm/bookings/${b.id}/weighed`, {
                        weight_kg: Number(weights[b.id]) || 0 });
                      setWeights({ ...weights, [b.id]: "" });
                    }, "Weighed in")}>
                    Weigh it in
                  </Button>
                  {b.payment_status === "unpaid" && (
                    <Button size="sm" variant="outline" data-testid={`field-settle-${b.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/bookings/${b.id}/settled`), "Settled at the gate")}>
                      Paid at the gate
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mine && (
        <div className="border border-border bg-white p-4" data-testid="field-open-box">
          <p className="overline text-slate-500">Open a row for picking</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input data-testid="field-title" placeholder="Field name — 'Top orchard, Bramleys'"
              value={field.title} onChange={(e) => setField({ ...field, title: e.target.value })} />
            <select data-testid="field-crop" value={field.crop} aria-label="Crop"
              onChange={(e) => setField({ ...field, crop: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.crops || []).map((c) => <option key={c} value={c}>{String(c)}</option>)}
            </select>
            <select data-testid="field-basis" value={field.price_basis} aria-label="Priced"
              onChange={(e) => setField({ ...field, price_basis: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.price_basis || []).map((b) => <option key={b} value={b}>{String(b)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Price
              <Input data-testid="field-price" type="number" min={0} step="0.01" value={field.price}
                onChange={(e) => setField({ ...field, price: e.target.value })} className="w-24" />
            </label>
            <Input data-testid="field-from" placeholder="Open from (e.g. 2026-08-20)"
              value={field.open_from} onChange={(e) => setField({ ...field, open_from: e.target.value })} />
            <Input data-testid="field-to" placeholder="Open to"
              value={field.open_to} onChange={(e) => setField({ ...field, open_to: e.target.value })} />
            <Input data-testid="field-slots" placeholder="Slot times — '09:00, 13:00'"
              value={field.slot_times} onChange={(e) => setField({ ...field, slot_times: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              People per slot
              <Input data-testid="field-capacity" type="number" min={1} value={field.people_per_slot}
                onChange={(e) => setField({ ...field, people_per_slot: e.target.value })} className="w-20" />
            </label>
            <Input data-testid="field-bring" placeholder="What to bring — 'boots and a bag'"
              value={field.bring} onChange={(e) => setField({ ...field, bring: e.target.value })} />
            <Input data-testid="field-note" placeholder="Note"
              value={field.note} onChange={(e) => setField({ ...field, note: e.target.value })} />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(data.days || []).map((d) => (
              <button key={d} type="button" data-testid={`field-day-pick-${d}`}
                onClick={() => toggleDay(d)}
                className={`tap-safe border px-2 py-1 text-xs transition-colors duration-150 ${
                  field.days.includes(d)
                    ? "border-[#0B5C2E] bg-[#0B5C2E] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0B5C2E]"}`}>
                {String(d).slice(0, 3)}
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {FLAGS.map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid={`field-flag-${key}`} checked={!!field[key]}
                  onChange={(e) => setField({ ...field, [key]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
          <Button className="mt-3" size="sm" data-testid="field-save"
            disabled={busy || field.title.trim().length < 3}
            onClick={() => act(async () => {
              await api.post("/farm/fields", {
                stall_id: mine.id, crop: field.crop, title: field.title,
                price_basis: field.price_basis, price: Number(field.price) || 0,
                open_from: field.open_from, open_to: field.open_to, days: field.days,
                slot_times: field.slot_times.split(",").map((s) => s.trim()).filter(Boolean),
                people_per_slot: Number(field.people_per_slot) || 20,
                bring: field.bring, note: field.note,
                children_ok: field.children_ok, dogs_ok: field.dogs_ok,
                wheelchair_access: field.wheelchair_access, parking: field.parking,
                picnic_area: field.picnic_area, tractor_ride: field.tractor_ride,
                honesty_box: field.honesty_box,
              });
              setField({ ...field, title: "", note: "" });
              reload?.();
            }, "The row is open for picking")}>
            Open the field
          </Button>
        </div>
      )}
    </div>
  );
};
